import React, { useMemo, useState } from 'react';
import {
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Tooltip from '@src/components/Tooltip';
import BadgeAvatar from '@src/components/BadgeAvatarNew';
import SpeedNewV2 from '@src/new-components/Speed';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import RunningWithErrorsRoundedIcon from '@mui/icons-material/RunningWithErrorsRounded';
import SlowMotionVideoRoundedIcon from '@mui/icons-material/SlowMotionVideoRounded';
import LoadingCircular from '@src/new-components/LoadingCircular';
import { convertFormatDate } from '@src/utils/date';
import LimitedFeature from '@src/components/LimitedFeature';
import CommonButton from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import { useSelector, useDispatch } from 'react-redux';
import actions from '@src/redux/actions';
import {
  getSpeedFromBlockSelected,
  getEmphasisFromBlockSelected,
  getSpeakerFromBlockSelected,
  getVoiceFromBlockSelected,
  getVideoDubbingBlockChanged,
  isVideoSource,
  calculateBlocksSynthesisCredits,
  pickConvertibleBlocks,
  getEffectiveText,
  isBlockRewriting,
  getExportingState,
} from '@src/services/project';
import EmphasisPopover from '@src/containers/EmphasisPopover';
import SpeakerSelectionPopover from '@src/containers/ProjectDetail/Dubbing/SpeakerSelectionPopover';
import AddSpeaker from '@src/containers/ProjectDetail/Dubbing/AddSpeaker';
import VoiceSelectionDialog from '@src/containers/VoiceSelectionDialog';
import {
  BLOCK_STATUS,
  CONVERTING_STATUS,
  PROJECT_PRODUCT,
  TRANSLATION_STATUS,
} from '@src/constants/project';
import { EXPORT_TYPE } from '@src/constants/presentation';
import moment from 'moment';
import { SUB_STATUS } from '@src/constants/voiceCloning';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useExportAudio from '@src/hooks/useExportAudio';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import { downloadFileByUrl, getFileName } from '@src/utils/file';
import { COLOR } from '@src/styles/color';
import { HISTORY_LABEL, HISTORY_SCOPE } from '@src/constants/history';
import { StyledConfigMultiBlock } from './index.style';
import SpeakerOption from '../../SpeakerOption';

const ConfigMultiBlock = ({
  setDeleteDialog,
  filterBlockResults,
  isBlockUIImprovement,
  handleConvertBlockSelected,
  canDownloadBlockAudio,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { selectedBlockIds, isProjectSynthesizing } = useSelector(
    (state) => state.project,
  );
  const { user } = useSelector((state) => state.auth);
  const {
    audioType,
    originalAudioType,
    voiceDefault,
    currentFilteringStatus,
    currentFilteringVoiceCode,
    speakers,
    projectId,
    exports,
    translation,
    product,
    source,
    isDownloading,
  } = useSelector((state) => state.project);
  const multiBlock = useSelector((state) => state.project.multiBlock);
  const { blockIds, blocks, originalBlocks, originalBlockIds } =
    multiBlock || {};
  const dispatch = useDispatch();
  const speed = getSpeedFromBlockSelected(selectedBlockIds, blocks);
  const emphasis = getEmphasisFromBlockSelected(selectedBlockIds, blocks);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [openAddSpeaker, setOpenAddSpeaker] = useState(false);
  const [openVoices, setOpenVoices] = useState(false);
  const { isExporting, startExport } = useExportAudio({
    projectId,
    exportType: EXPORT_TYPE.SEPARATE_AUDIO_PARTIAL,
  });
  const { getFeatureValue } = useFeatureFlags();

  const speakerConfigEnabled = getFeatureValue(FEATURE_KEYS.SPEAKER_CONFIG, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });
  const undoRedoEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_UNDO_REDO,
    { userId: user?.id, email: user?.email, phoneNumber: user?.phoneNumber },
  );

  const blocksSelected = useMemo(
    () => selectedBlockIds.map((blockId) => blocks[blockId]),
    [blockIds, blocks, selectedBlockIds],
  );
  const isSynthesizing = isProjectSynthesizing;

  const useTranslatedText = translation?.status === TRANSLATION_STATUS.SUCCESS;

  // What converting this selection would actually submit — the same list the
  // container prices in the confirmation and then sends, so the tooltip can
  // never quote a different number.
  const convertibleSelectedBlocks = useMemo(
    () => pickConvertibleBlocks(blocksSelected, { useTranslatedText }),
    [blocksSelected, useTranslatedText],
  );
  const selectedBlocksCredits = useMemo(
    () =>
      calculateBlocksSynthesisCredits(convertibleSelectedBlocks, {
        speakers,
        voiceDefault,
        useTranslatedText,
      }),
    [convertibleSelectedBlocks, speakers, voiceDefault, useTranslatedText],
  );

  // Enable/disable the multi-block convert button (mirrors TTS
  // ConfigMultiBlock). Enabled when there is at least one selected block that is
  // not all-in-progress and not all empty-text.
  const blockSynthesizing = blocksSelected.filter(
    (block) => block?.blockStatus === BLOCK_STATUS.IN_PROGRESS,
  );
  // Stricter than the in-flight rule one row above, and deliberately so: ONE
  // selected block being rewritten disables the button outright. Filtering it
  // out of the payload instead would leave a live button that silently converts
  // less than the selection it is pointing at. Outside the `blockAvailable`
  // short-circuit, since a block rewritten before it ever had audio is DRAFT.
  const blockRewriting = blocksSelected.filter((block) =>
    isBlockRewriting(block),
  );
  const blockTextEmpty = blocksSelected?.filter(
    (block) => !getEffectiveText(block, useTranslatedText).length,
  );
  const blockAvailable = blocksSelected?.filter((block) =>
    [
      BLOCK_STATUS.DRAFT,
      BLOCK_STATUS.AUDIO_EXPIRED,
      BLOCK_STATUS.ERROR,
    ].includes(block?.blockStatus),
  );
  const disableConvertBtn =
    blockTextEmpty?.length > 0 &&
    blockAvailable?.length === blockTextEmpty?.length;
  const isEnableConvertBtn =
    blocksSelected?.length > 0 &&
    (blockAvailable.length ||
      blockSynthesizing?.length !== blocksSelected?.length) &&
    !blockRewriting?.length &&
    !disableConvertBtn;

  // Multi-block download (AIV-12773 US-10.2): mirrors TTS. Enabled only when
  // every selected block with text has an available (still-stored) audio.
  const blockHasText = blocksSelected?.filter(
    (block) => getEffectiveText(block, useTranslatedText).length,
  );
  const isEnableDownloadBtn =
    blocksSelected?.length > 0 &&
    blockHasText?.length > 0 &&
    blockHasText?.every(
      (block) => block?.blockStatus === BLOCK_STATUS.AUDIO_AVAILABLE,
    );

  // Current selection in project block order, as the export API expects.
  const selectedIdsInProjectOrder = useMemo(
    () => (blockIds || []).filter((id) => selectedBlockIds.includes(id)),
    [blockIds, selectedBlockIds],
  );

  const isTranslationSuccess =
    translation?.status === TRANSLATION_STATUS.SUCCESS;

  const exportSeparateAudioPartial =
    exports?.[EXPORT_TYPE.SEPARATE_AUDIO_PARTIAL];

  // Reuse the cached export only when nothing that changes its content moved:
  // same selection (as a set), no selected block edited, project block set
  // unchanged (adds/removes shift zip entry numbering), same audio format.
  const canReuseSeparateAudioPartial = useMemo(() => {
    const storedBlockIds = exportSeparateAudioPartial?.blockIds;
    const sameSelection =
      Array.isArray(storedBlockIds) &&
      storedBlockIds.length === selectedIdsInProjectOrder.length &&
      selectedIdsInProjectOrder.every((id) => storedBlockIds.includes(id));
    if (!exportSeparateAudioPartial?.fileUrl || !sameSelection) return false;

    // Only blocks with (translation-aware) text feed the export payload.
    const selectedBlocksWithText = blocksSelected.filter((block) =>
      isTranslationSuccess ? block?.translatedText : block?.text,
    );
    // The export zips raw per-block audio with no timing, so endTime changes
    // are ignored.
    const selectedBlocksChanged = getVideoDubbingBlockChanged(
      selectedBlocksWithText,
      originalBlocks || {},
    );

    return (
      selectedBlocksChanged?.length === 0 &&
      blockIds?.length === originalBlockIds?.length &&
      originalAudioType === audioType
    );
  }, [
    exportSeparateAudioPartial,
    selectedIdsInProjectOrder,
    blocksSelected,
    isTranslationSuccess,
    originalBlocks,
    blockIds,
    originalBlockIds,
    originalAudioType,
    audioType,
  ]);

  const handleDownloadMultiBlock = () => {
    if (canReuseSeparateAudioPartial) {
      downloadFileByUrl(
        exportSeparateAudioPartial.fileUrl,
        getFileName(exportSeparateAudioPartial.fileUrl),
      );
      return;
    }
    startExport(selectedIdsInProjectOrder);
  };

  const speaker = speakerConfigEnabled
    ? getSpeakerFromBlockSelected({
        selectedBlockIds,
        blocks,
        speakers,
      })
    : null;
  const voice = speakerConfigEnabled
    ? speaker?.voice
    : getVoiceFromBlockSelected({
        selectedBlockIds,
        blocks,
        voiceDefault,
      });

  const isInValidPublicVoiceCloning =
    voice?.userId !== user?.id &&
    voice?.discardAt &&
    moment().isAfter(voice?.discardAt);

  const isPublicVoiceCloning = voice?.status === SUB_STATUS.PUBLIC;
  const isVoiceCloningAboutToRemoved =
    isPublicVoiceCloning && voice?.discardAt
      ? moment().isBefore(voice?.discardAt)
      : false;

  const speakerName = speaker?.name || t('chooseSpeaker');
  const allBlocksHaveEmphasis = blocksSelected?.every(
    (block) => block?.hasEmphasis,
  );

  // Lock select-all while the create-audio flow (flag + DUBBING + video source)
  // is converting any block or downloading, matching the per-block checkbox.
  const canCreateBlockAudio =
    getFeatureValue(FEATURE_KEYS.VIDEO_DUBBING_CREATE_AUDIO, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    }) &&
    product === PROJECT_PRODUCT.DUBBING &&
    isVideoSource(source);
  // A download is assembled from the blocks as they stand, so anything that
  // edits them has to wait for it.
  //
  // `isDownloading` is the synchronous half: a file the browser builds itself,
  // read block by block while it runs. The async half reports through
  // `exports[type].status` and leaves `isDownloading` false the whole time —
  // `isProjectExporting` covers the project-wide types, `isExporting` this
  // toolbar's own partial-audio export.
  //
  // Behind the export-history flag only the synchronous half locks: the backend
  // snapshots the project when an async export starts, and a partial export
  // carries the block ids it was started with.
  const exportHistoryEnabled = useExportHistoryEnabled();
  const { isExporting: isProjectExporting } = getExportingState({
    exports,
    isDownloading,
    product,
  });
  const isDownloadInFlight =
    isDownloading ||
    ((isProjectExporting || isExporting) && !exportHistoryEnabled);

  // Selection must not change under a running operation: it decides what the
  // in-flight download contains. The block-status half stays behind
  // `canCreateBlockAudio` (only that flow converts per block); a download locks
  // the selection either way.
  const isBlockSelectionLocked =
    isDownloadInFlight ||
    (canCreateBlockAudio &&
      Object.values(blocks || {}).some(
        (block) => block?.status === CONVERTING_STATUS.IN_PROGRESS,
      ));

  const isDisableCheckbox =
    Object.keys(filterBlockResults)?.length === 0 || isBlockSelectionLocked;

  // Every control in this row edits the selected blocks, so they all freeze
  // while a download is being built from them, and while any of those blocks is
  // being synthesized — the synthesis reads the voice and speed these controls
  // change. `isSynthesizing` is the project-wide flag and only the legacy
  // whole-project flow raises it, so the selection has to be read too. One busy
  // block is enough, the same rule `blockRewriting` already applies. Download
  // and delete are the exceptions: one IS the operation in flight, the other is
  // the only way out of a block stuck in progress.
  const isEditingLocked =
    isSynthesizing || isDownloadInFlight || blockSynthesizing?.length > 0;

  const handleChangeSelectAll = (e) => {
    const isChecked = e.target.checked;

    // If unchecked or already have selections, unselect all
    if (!isChecked || selectedBlockIds.length > 0) {
      dispatch(actions.project.unselectAllBlocks());
      return;
    }
    const blocksFilter = Object.keys(filterBlockResults);
    // If nothing is selected and the event is "select all"
    const filteredBlockIds = blocksFilter.filter((blockId) => {
      const block = blocks[blockId];
      // When filtering ALL, include blocks matching currentFilteringVoiceCode (or all if code empty)
      if (
        currentFilteringStatus === BLOCK_STATUS.ALL ||
        currentFilteringVoiceCode === ''
      ) {
        const blockVoiceCode = block.voice?.code || voiceDefault?.code;
        return (
          currentFilteringVoiceCode === '' ||
          blockVoiceCode === currentFilteringVoiceCode
        );
      }
      // Otherwise, only blocks with current filtering status
      return (
        block.blockStatus === currentFilteringStatus &&
        (block.voice?.code || voiceDefault?.code) === currentFilteringVoiceCode
      );
    });

    dispatch(actions.project.selectBlocks(filteredBlockIds));
  };
  const isChecked =
    selectedBlockIds.length === blockIds.length &&
    selectedBlockIds.every((item) => blockIds.includes(item));

  const handleOpenSpeakers = (event) => {
    if (speakerConfigEnabled) {
      // When speaker config is enabled, open speaker selection popover
      setPopoverAnchorEl(event?.currentTarget);
    } else {
      // When speaker config is disabled, open voice dialog directly (like TTS)
      setOpenVoices(true);
    }
  };

  const handleCloseVoices = () => {
    setOpenVoices(false);
  };

  const handleClosePopover = () => {
    setPopoverAnchorEl(null);
  };

  const handleOpenAddSpeaker = () => {
    setPopoverAnchorEl(null);
    setOpenAddSpeaker(true);
  };

  const handleCloseAddSpeaker = () => {
    setOpenAddSpeaker(false);
  };

  const handleChangeVoice = (newVoice) => {
    // When speaker config is disabled, handle voice change directly (like TTS)
    const isVoiceHasEmphasis = newVoice?.hasEmphasis;
    const blockUpdates = selectedBlockIds.map((blockId) => ({
      blockId,
      block: {
        voice: newVoice,
        voiceCode: newVoice?.code,
        hasEmphasis: isVoiceHasEmphasis,
        emphasis: isVoiceHasEmphasis ? blocks[blockId]?.emphasis : 0,
      },
    }));
    dispatch(actions.project.updateMultipleBlocks(blockUpdates));
  };

  const handleChangeSpeaker = (newSpeaker) => {
    if (!speakerConfigEnabled) return;
    const blockUpdates = selectedBlockIds.map((blockId) => {
      const isVoiceHasEmphasis = newSpeaker?.voice?.hasEmphasis;

      return {
        blockId,
        block: {
          voiceCode: newSpeaker?.voice?.code,
          speed: newSpeaker?.speed,
          speakerId: newSpeaker?.id,
          voice: {
            code: newSpeaker?.voice?.code,
            name: newSpeaker?.voice?.name,
            roundImage: newSpeaker?.voice?.roundImage,
            language: {
              roundImage: newSpeaker?.voice?.language?.roundImage,
            },
            level: newSpeaker?.voice?.level,
            provider: newSpeaker?.voice?.provider,
            eolDate: newSpeaker?.voice?.eolDate,
            creditFactor: newSpeaker?.voice?.creditFactor,
            canonicalVoice: newSpeaker?.voice?.canonicalVoice,
            discardAt: newSpeaker?.voice?.discardAt,
            retentionDays: newSpeaker?.voice?.retentionDays,
            status: newSpeaker?.voice?.status,
            userId: newSpeaker?.voice?.userId,
            features: Array.isArray(newSpeaker?.voice?.features)
              ? newSpeaker?.voice?.features
              : [],
          },
          hasEmphasis: isVoiceHasEmphasis,
          emphasis: isVoiceHasEmphasis ? blocks[blockId]?.emphasis : 0,
        },
      };
    });
    const options = undoRedoEnabled
      ? {
          history: {
            commit: true,
            scope: HISTORY_SCOPE.DUBBING,
            label: HISTORY_LABEL.CHANGE_BLOCK_VOICE,
          },
        }
      : {};
    dispatch(actions.project.updateMultipleBlocks(blockUpdates, options));
  };

  const handleChangeSpeed = (name, value) => {
    const blockUpdates = selectedBlockIds.map((blockId) => ({
      blockId,
      block: { speed: value },
    }));
    const options = undoRedoEnabled
      ? {
          history: {
            commit: true,
            scope: HISTORY_SCOPE.DUBBING,
            label: HISTORY_LABEL.CHANGE_BLOCK_SPEED,
          },
        }
      : {};
    dispatch(actions.project.updateMultipleBlocks(blockUpdates, options));
  };

  const handleChangeEmphasis = (name, value) => {
    const blockUpdates = selectedBlockIds.map((blockId) => ({
      blockId,
      block: { emphasis: value },
    }));
    const options = undoRedoEnabled
      ? {
          history: {
            commit: true,
            scope: HISTORY_SCOPE.DUBBING,
            label: HISTORY_LABEL.CHANGE_BLOCK_EMPHASIS,
          },
        }
      : {};
    dispatch(actions.project.updateMultipleBlocks(blockUpdates, options));
  };

  return (
    <>
      <StyledConfigMultiBlock>
        <FormControlLabel
          className="checkbox-container"
          style={isBlockUIImprovement ? { paddingLeft: '8px' } : undefined}
          control={
            <Checkbox
              className={`checkbox-color ${
                isDisableCheckbox ? 'disabled-btn' : ''
              }`}
              indeterminate={
                selectedBlockIds.length > 0 &&
                selectedBlockIds.length < blockIds.length
              }
              disabled={isDisableCheckbox}
            />
          }
          labelPlacement="end"
          disabled={isDisableCheckbox}
          label={
            selectedBlockIds.length > 0
              ? t('deselectAllV2', {
                  currentSelected: selectedBlockIds.length,
                  totalSelected: Object.keys(filterBlockResults)?.length,
                })
              : t('selectAll')
          }
          onChange={handleChangeSelectAll}
          checked={isChecked}
          classes={{ label: 'checkbox-label' }}
        />
        {selectedBlockIds.length > 0 && (
          <>
            <Divider orientation="vertical" flexItem />
            {speakerConfigEnabled ? (
              <SpeakerOption
                handleOpenVoices={handleOpenSpeakers}
                isSynthesizing={isEditingLocked}
                voice={voice}
                isInValidPublicVoiceCloning={isInValidPublicVoiceCloning}
                isVoiceCloningAboutToRemoved={isVoiceCloningAboutToRemoved}
                openVoice={Boolean(popoverAnchorEl)}
                speakerName={speakerName}
              />
            ) : (
              <div className="config-multi-block-voice">
                <Tooltip
                  arrow
                  title={isEditingLocked ? '' : t('chooseVoice')}
                  placement="top"
                >
                  <CommonButton
                    variant="transparent"
                    onClick={handleOpenSpeakers}
                    data-id="open-voice-list"
                    customStyle={{
                      gap: '8px',
                      padding: '4px 6px',
                      borderRadius: '16px',
                      '&:focus': {
                        backgroundColor: 'transparent',
                      },
                      '&:hover': {
                        background: COLOR.secondary[80],
                      },
                      '&:disabled': {
                        opacity: 0.5,
                        cursor: 'not-allowed',
                        pointerEvents: 'auto',
                        '&:hover': {
                          backgroundColor: COLOR.white,
                          boxShadow: 'none',
                        },
                      },
                    }}
                    disabled={isEditingLocked}
                  >
                    <BadgeAvatar
                      width={25}
                      smallImgWidth={12}
                      img={voice?.roundImage}
                      smallImg={voice?.language?.roundImage}
                      type="image"
                      newUiAvatar
                    />
                    <Typography
                      variant="body-14-medium"
                      color={
                        isInValidPublicVoiceCloning
                          ? COLOR.redPrimary
                          : COLOR.indigo.default
                      }
                    >
                      {voice?.name || t('chooseVoice')}
                    </Typography>
                  </CommonButton>
                </Tooltip>
                <LimitedFeature voiceFeatures={voice?.features} />
                {isVoiceCloningAboutToRemoved && (
                  <Tooltip
                    arrow
                    title={t('voiceCloningDiscardAt', {
                      date: convertFormatDate(voice?.discardAt),
                    })}
                    placement="top"
                  >
                    <RunningWithErrorsRoundedIcon className="returning-error-icon" />
                  </Tooltip>
                )}
              </div>
            )}
            <SpeedNewV2
              currentSpeed={speed}
              disabled={isEditingLocked}
              onChange={handleChangeSpeed}
            />
            {allBlocksHaveEmphasis && (
              <EmphasisPopover
                currentEmphasis={emphasis}
                onChangeEmphasis={handleChangeEmphasis}
                isDisabled={isEditingLocked}
              />
            )}
            <Divider orientation="vertical" flexItem />
            <Box display="flex" alignItems="center" gap={1}>
              {handleConvertBlockSelected && (
                <Tooltip
                  sx={{ display: 'block' }}
                  arrow
                  title={t('costsCredit', {
                    value: selectedBlocksCredits?.toLocaleString(language),
                  })}
                  placement="top"
                >
                  <div
                    className={
                      isEnableConvertBtn && !isEditingLocked
                        ? ''
                        : 'disabled-btn'
                    }
                  >
                    <IconButton
                      onClick={() => handleConvertBlockSelected(blocksSelected)}
                      className="editor-toolbar-btn"
                      disabled={!isEnableConvertBtn || isEditingLocked}
                    >
                      <SlowMotionVideoRoundedIcon className="editor-toolbar-btn-icon" />
                    </IconButton>
                  </div>
                </Tooltip>
              )}
              {canDownloadBlockAudio && (
                <Tooltip
                  sx={{
                    display:
                      isEnableDownloadBtn && !isExporting ? 'block' : 'none',
                  }}
                  arrow
                  title={t('download')}
                  placement="top"
                >
                  <div className={isEnableDownloadBtn ? '' : 'disabled-btn'}>
                    {isExporting ? (
                      <IconButton className="editor-toolbar-btn">
                        <LoadingCircular variant="base" />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={handleDownloadMultiBlock}
                        className="editor-toolbar-btn"
                        disabled={!isEnableDownloadBtn || isExporting}
                      >
                        <DownloadRoundedIcon className="editor-toolbar-btn-icon" />
                      </IconButton>
                    )}
                  </div>
                </Tooltip>
              )}
              <Tooltip
                sx={{
                  display: 'block',
                }}
                arrow
                title={t('delete')}
                placement="top"
              >
                <div className={isDownloadInFlight ? 'disabled-btn' : ''}>
                  <IconButton
                    onClick={() => setDeleteDialog(true)}
                    className="editor-toolbar-btn"
                    disabled={isDownloadInFlight}
                  >
                    <DeleteIcon className="editor-toolbar-btn-icon" />
                  </IconButton>
                </div>
              </Tooltip>
            </Box>
          </>
        )}
      </StyledConfigMultiBlock>
      {speakerConfigEnabled ? (
        <>
          <SpeakerSelectionPopover
            open={Boolean(popoverAnchorEl)}
            anchorEl={popoverAnchorEl}
            onClose={handleClosePopover}
            speakers={speakers}
            currentSpeaker={speaker}
            voiceDefault={voiceDefault}
            onAddSpeaker={handleOpenAddSpeaker}
            onSelectSpeaker={handleChangeSpeaker}
            user={user}
          />
          <AddSpeaker
            open={openAddSpeaker}
            onClose={handleCloseAddSpeaker}
            isApplyPreset
            projectId={projectId}
            selectedBlockIds={selectedBlockIds}
          />
        </>
      ) : (
        <VoiceSelectionDialog
          open={openVoices}
          activeVoiceId={voice && voice.id}
          activeVoiceCode={voice && voice.code}
          onClose={handleCloseVoices}
          onChangeVoice={handleChangeVoice}
          hasLanguageFilter
        />
      )}
    </>
  );
};

export default ConfigMultiBlock;
